import { useState } from 'react';
import { FileText, FileCheck2, Loader2 } from 'lucide-react';
import { apiClient } from '@/api/client';
import { cn } from '@/lib/utils';

type Size = 'xs' | 'sm';

/**
 * Which document the button opens. Mirrors the pills on the ticket detail
 * ("Firmado" green / "Sin firmar" amber) so the same artifact reads the same
 * way in a list and on the detail page.
 */
export type PdfTone = 'firmado' | 'borrador' | 'neutro';

interface PdfIconButtonProps {
  ticketId: number;
  size?: Size;
  /** Stop propagation on click — useful inside clickable card containers. */
  stopPropagation?: boolean;
  tone?: PdfTone;
  /** Show the tone's word beside the icon. For rows with horizontal room. */
  labeled?: boolean;
}

const SIZE_CLASSES: Record<Size, string> = {
  xs: 'p-1 h-5 w-5',
  sm: 'p-1.5 h-6 w-6',
};

const ICON_SIZE: Record<Size, string> = {
  xs: 'h-3 w-3',
  sm: 'h-4 w-4',
};

const TONES: Record<PdfTone, { Icon: typeof FileText; className: string; label: string; title: string }> = {
  // "PDF firmado" is the ticket's own work order, NOT a certification
  // document — those are the certificados adjuntos (see DocumentosBadge).
  // The wording keeps the two apart on screen.
  firmado: {
    Icon: FileCheck2,
    className: 'text-green-700 hover:bg-green-500/10 dark:text-green-400',
    label: 'PDF firmado',
    title: 'Ver PDF del ticket (firmado)',
  },
  borrador: {
    Icon: FileText,
    className: 'text-amber-600 hover:bg-amber-500/10 dark:text-amber-400',
    label: 'PDF sin firmar',
    title: 'Ver PDF del ticket (sin firmar)',
  },
  neutro: {
    Icon: FileText,
    className: 'text-sime-danger hover:bg-sime-danger/10',
    label: 'PDF',
    title: 'Ver PDF',
  },
};

/**
 * Renders an inline PDF-link button for a ticket. Resolves the presigned S3
 * URL on click (avoids prefetching for 200+ rows on a calendar grid) and
 * opens it in a new tab.
 *
 * Callers must know a document actually exists — pass the ticket's
 * `documentos.pdfEstado` from the API rather than inferring it from
 * `estadoOt`, which on legacy periods marks hundreds of tickets that have no
 * document at all. `DocumentosBadge` does this for every list surface.
 */
export function PdfIconButton({
  ticketId,
  size = 'sm',
  stopPropagation = true,
  tone = 'neutro',
  labeled = false,
}: PdfIconButtonProps) {
  const [loading, setLoading] = useState(false);
  const { Icon, className, label, title } = TONES[tone];

  const handleClick = async (e: React.MouseEvent) => {
    if (stopPropagation) e.stopPropagation();
    setLoading(true);
    try {
      const res = await apiClient<{ success: true; data: { hasPdf: boolean; url: string } }>(
        `/tickets/${ticketId}/pdf`,
      );
      if (res.data.hasPdf && res.data.url) {
        window.open(res.data.url, '_blank', 'noopener');
      } else {
        alert('PDF no disponible para este ticket.');
      }
    } catch {
      alert('Error al obtener el PDF.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={(e) => { void handleClick(e); }}
      disabled={loading}
      className={cn(
        'rounded-md transition-colors disabled:opacity-50',
        'inline-flex items-center justify-center',
        className,
        labeled
          ? 'gap-1 px-1.5 py-0.5 text-[10px] font-semibold leading-none'
          : SIZE_CLASSES[size],
      )}
      aria-label={title}
      title={title}
    >
      {loading ? (
        <Loader2 className={cn(ICON_SIZE[size], 'animate-spin')} />
      ) : (
        <Icon className={ICON_SIZE[size]} />
      )}
      {labeled ? label : null}
    </button>
  );
}
